/**
 * SkeletonSection — Placeholder cards shown during first build
 *
 * Variants: dashboards, insights, datasets
 * Shows animated skeleton cards with contextual "in progress" messages.
 */

import React from 'react';
import {
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';

// ─── Styles ──────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  grid: {
    display: 'grid',
    gap: '12px',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    ...shorthands.padding('20px'),
    ...shorthands.borderRadius('10px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke3),
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: '120px',
  },
  skeletonBar: {
    height: '10px',
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground4,
    animationName: {
      '0%': { opacity: 0.4 },
      '50%': { opacity: 0.7 },
      '100%': { opacity: 0.4 },
    },
    animationDuration: '1.8s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  skeletonBarShort: {
    width: '60%',
  },
  skeletonBarMedium: {
    width: '80%',
  },
  skeletonBarFull: {
    width: '100%',
  },
  skeletonChart: {
    height: '48px',
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorNeutralBackground4,
    animationName: {
      '0%': { opacity: 0.3 },
      '50%': { opacity: 0.55 },
      '100%': { opacity: 0.3 },
    },
    animationDuration: '2s',
    animationIterationCount: 'infinite',
    animationTimingFunction: 'ease-in-out',
  },
  message: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type SkeletonVariant = 'dashboards' | 'insights' | 'datasets';

interface SkeletonSectionProps {
  variant: SkeletonVariant;
}

// ─── Config ──────────────────────────────────────────────────────────────────

const VARIANT_CONFIG: Record<SkeletonVariant, { title: string; message: string; count: number }> = {
  dashboards: {
    title: 'Recommended Dashboards',
    message: 'AI is designing dashboards…',
    count: 3,
  },
  insights: {
    title: 'Key Insights',
    message: 'Searching for useful business patterns…',
    count: 3,
  },
  datasets: {
    title: 'Business Datasets',
    message: 'Analyzing your business structure…',
    count: 2,
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

const SkeletonSection: React.FC<SkeletonSectionProps> = ({ variant }) => {
  const styles = useStyles();
  const config = VARIANT_CONFIG[variant];

  return (
    <div className={styles.section}>
      <Text className={styles.sectionTitle}>{config.title}</Text>
      <div className={styles.grid}>
        {Array.from({ length: config.count }).map((_, idx) => (
          <div key={idx} className={styles.card}>
            <div className={`${styles.skeletonBar} ${styles.skeletonBarMedium}`} />
            <div className={`${styles.skeletonBar} ${styles.skeletonBarFull}`} />
            <div className={`${styles.skeletonBar} ${styles.skeletonBarShort}`} />
            {variant === 'dashboards' && <div className={styles.skeletonChart} />}
          </div>
        ))}
      </div>
      <Text className={styles.message}>{config.message}</Text>
    </div>
  );
};

export default SkeletonSection;